// Title and description per route. vite.config.ts writes a copy of index.html with
// these tags for every route, so link previews and crawlers see the right page
// without running the app; main.tsx sets the same title in the browser.
// Plain data only: this module is also imported by the build config.

export const SITE = "https://pantherterminal.com";

export interface RouteMeta {
  path: string;
  title: string;
  description: string;
}

export const ROUTE_META: readonly RouteMeta[] = [
  {
    // "/" redirects to /tokenization (U-18); this is the SPA fallback page's copy.
    path: "/",
    title: "Panther Terminal — Tokenized assets, live on-chain",
    description: "Live on-chain data on tokenized assets: stocks, stablecoins, U.S. Treasuries, government debt and private credit, refreshed every 30 minutes.",
  },
  {
    path: "/tokenization",
    title: "Tokenized assets — Panther Terminal",
    description: "Tokenized real-world assets live on-chain: Treasuries, credit, stocks, commodities, stablecoins and more, ranked by network, issuer and class, refreshed every 30 minutes.",
  },
  {
    path: "/tokenization/leagues",
    title: "League tables — Panther Terminal",
    description: "Who leads tokenized assets: value ranked by network, by issuer or manager and by platform, across every asset class or within one.",
  },
  {
    path: "/tokenization/stablecoins",
    title: "Stablecoins — Panther Terminal",
    description: "Live stablecoin supply by coin, issuer and chain, refreshed every 30 minutes.",
  },
  {
    path: "/tokenization/treasuries",
    title: "U.S. Treasuries — Panther Terminal",
    description: "Tokenized U.S. Treasury funds and bills: on-chain supply, NAV, value and yield by issuer and chain.",
  },
  {
    path: "/tokenization/govt-debt",
    title: "Non-U.S. Govt Debt — Panther Terminal",
    description: "Tokenized non-U.S. government debt: euro, sterling and emerging-market government bond tokens across chains.",
  },
  {
    path: "/tokenization/credit",
    title: "Private Credit — Panther Terminal",
    description: "Tokenized private credit funds, pools and lending protocols: token market cap and protocol TVL by issuer and chain.",
  },
  {
    path: "/tokenization/money-market",
    title: "Money Market — Panther Terminal",
    description: "Tokenized money market and cash funds that are not government funds: prime money market and overnight swap funds, with on-chain supply, NAV and value by issuer and chain.",
  },
  {
    path: "/tokenization/commodities",
    title: "Commodities — Panther Terminal",
    description: "Tokenized gold, silver and other commodities: on-chain supply, price per troy ounce or gram, and value by issuer and chain.",
  },
  {
    path: "/tokenization/private-equity",
    title: "Private Equity — Panther Terminal",
    description: "Tokenized venture fund interests and pre-IPO products, with what each holder actually owns: fund interests, pre-IPO notes, SPV interests and loan participations, valued by published NAV only.",
  },
  {
    path: "/tokenization/real-estate",
    title: "Real Estate — Panther Terminal",
    description: "Tokenized real estate: REIT shares, property-vehicle tokens and whole platforms, with what each holder owns and where a value is only an offering price.",
  },
  {
    path: "/tokenization/onboarding",
    title: "Portfolio builder — Panther Terminal",
    description: "Build a stock and bond portfolio from tokenized funds: a mean-variance mix, a glide path, and where each order fills best on chain for its exact size. Educational, not advice.",
  },
  {
    path: "/tokenization/methodology",
    title: "How we count — Panther Terminal",
    description: "How Panther Terminal measures supply, price, market cap and flows, and why an unknown value is never shown as zero.",
  },
  {
    path: "/investment-analysis",
    title: "Investment Analysis — Panther Research",
    description: "Follow the flows. Find your edge.",
  },
  {
    path: "/research",
    title: "Research — Panther Research",
    description: "Go deeper than the headline.",
  },
  {
    path: "/research/insider-trades",
    title: "Insider trades — Panther Research",
    description: "Look up any stock's insider buys and sells from SEC Form 4 filings, over any range back to 2004, pinned on its price chart and filtered by type and size.",
  },
  {
    path: "/daily-digest",
    title: "Daily Digest — Panther Research",
    description: "Your perspective on the market.",
  },
];

export const metaFor = (path: string): RouteMeta | undefined =>
  ROUTE_META.find((route) => route.path === (path || "/"));

const escapeAttr = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** index.html with the title, description, canonical, og:* and twitter:* tags set for one route. */
export function htmlForRoute(html: string, route: RouteMeta): string {
  const url = `${SITE}${route.path}`;
  const title = escapeAttr(route.title);
  const description = escapeAttr(route.description);
  const set = (source: string, pattern: RegExp, replacement: string) => {
    if (!pattern.test(source)) throw new Error(`index.html is missing ${pattern}`);
    return source.replace(pattern, replacement);
  };
  let out = html;
  out = set(out, /<title>[^<]*<\/title>/, `<title>${title}</title>`);
  out = set(out, /(<meta name="description" content=")[^"]*(")/, `$1${description}$2`);
  out = set(out, /(<link rel="canonical" href=")[^"]*(")/, `$1${url}$2`);
  out = set(out, /(<meta property="og:url" content=")[^"]*(")/, `$1${url}$2`);
  out = set(out, /(<meta property="og:title" content=")[^"]*(")/, `$1${title}$2`);
  out = set(out, /(<meta property="og:description" content=")[^"]*(")/, `$1${description}$2`);
  out = set(out, /(<meta name="twitter:title" content=")[^"]*(")/, `$1${title}$2`);
  out = set(out, /(<meta name="twitter:description" content=")[^"]*(")/, `$1${description}$2`);
  return out;
}
